import { useEffect, useState } from 'react';
import type { JSX } from 'react';
import { BOND_KINDS, BOND_LABELS, formatMm, shapeBounds } from '@bw/core';
import type { BondKind, LineKind, ShapeRole } from '@bw/core';
import type { SketchDrawMode } from '@bw/render-2d';
import { Trash2 } from 'lucide-react';
import type { EditorController } from '../editor/controller.js';
import { activeModel, useEditor } from '../state/store.js';
import { plural } from '../copy.js';

const ROLES: readonly { readonly id: ShapeRole; readonly label: string; readonly hint: string }[] = [
  { id: 'wall', label: 'Wall', hint: 'Brickwork: the shape a bond is laid into.' },
  { id: 'window', label: 'Window', hint: 'A hole, with a frame, glazing and a sill.' },
  { id: 'door', label: 'Door', hint: 'A hole, with a frame and a leaf.' },
  {
    id: 'flint',
    label: 'Flint wall',
    hint: 'Flint, not brick. No bricks are laid in it; it stands solid, jointed to the brickwork with mortar.',
  },
];

const KINDS: readonly { readonly id: LineKind; readonly label: string; readonly hint: string }[] = [
  { id: 'wall', label: 'Wall line', hint: 'Part of the wall. Drawn boldly, and can be filled.' },
  { id: 'construction', label: 'Construction line', hint: 'A guide only. Drawn faintly, never filled.' },
];

/**
 * Sketch mode: draw the outline of a wall, mark closed shapes as wall, window,
 * door or flint, then lay it by hand or fill it automatically.
 */
export function SketchPanel({ controller }: { readonly controller: EditorController | null }): JSX.Element {
  const project = useEditor((s) => s.project);
  useEditor((s) => s.docRevision);
  const model = project.activeWall.model;
  const [, setTick] = useState(0);

  useEffect(() => {
    if (controller === null) return;
    controller.onSketchChange(() => setTick((value) => value + 1));
    return () => controller.onSketchChange(null);
  }, [controller]);

  const bondCorners = useEditor((s) => s.bondCorners);
  const setBondCorners = useEditor((s) => s.setBondCorners);
  const corners = controller?.activeWallCorners() ?? 0;

  const sketch = model.sketch;
  const drafting = (controller?.draftPointCount ?? 0) > 0;
  const lineKind = controller?.lineKind ?? 'wall';
  const shapeRole = controller?.shapeRole ?? 'wall';
  const selectedId = controller?.selectedShapeId ?? null;
  const selection = controller?.sketchSelection ?? null;
  const selectedLineId = selection?.kind === 'segment' ? selection.id : null;

  return (
    <section>
      <h2>Sketch</h2>

      <div className="grid2">
        <label>
          Draw
          <select
            value={controller?.drawMode ?? 'polyline'}
            onChange={(event) => controller?.setDrawMode(event.target.value as SketchDrawMode)}
          >
            <option value="polyline">Free line</option>
            <option value="rectangle">Rectangle</option>
          </select>
        </label>
        <label>
          Line type
          <select value={lineKind} onChange={(event) => controller?.setLineKind(event.target.value as LineKind)}>
            {KINDS.map((kind) => (
              <option key={kind.id} value={kind.id}>
                {kind.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Closes as
          <select value={shapeRole} onChange={(event) => controller?.setShapeRole(event.target.value as ShapeRole)}>
            {ROLES.map((role) => (
              <option key={role.id} value={role.id}>
                {role.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <p className="note">{KINDS.find((kind) => kind.id === lineKind)?.hint}</p>
      <p className="note">{ROLES.find((role) => role.id === shapeRole)?.hint}</p>

      <p className="note">
        {controller?.drawMode === 'rectangle'
          ? 'Drag a rectangle on the drawing; it closes into a shape as soon as you let go.'
          : drafting
            ? `${plural(controller?.draftPointCount ?? 0, 'point')} placed. Click the first point, or press Enter, to close the shape. Esc keeps the lines open as guides.`
            : 'Click on the drawing to start a line. Points snap to drawn lines and corners, to the middle of a line, and to the corners and edges of laid brickwork — the drawing marks what each one caught. Hold Alt to put a point down anywhere.'}
      </p>
      {/* Editing is what the tool does when no run is in progress, so the
          gestures are worth stating: none of them is visible until tried. */}
      {!drafting && controller?.drawMode !== 'rectangle' && (
        <p className="note">
          Click a line to select its shape · drag a line to move the whole shape · drag a corner to
          move that corner · double-click a line to add a corner · Delete removes what is selected.
        </p>
      )}

      <div className="actions">
        <button className="btn" disabled={!drafting} onClick={() => controller?.undoSketchPoint()}>
          Undo point
        </button>
        <button
          className="btn primary"
          disabled={(controller?.draftPointCount ?? 0) < 3}
          onClick={() => controller?.closeSketchShape()}
        >
          Close shape
        </button>
      </div>

      <h2 style={{ marginTop: 16 }}>Shapes</h2>
      {sketch.shapes.length === 0 && sketch.segments.length === 0 && (
        <p className="note">Nothing drawn yet.</p>
      )}
      {sketch.segments.length > 0 && (
        <p className="note">
          {plural(sketch.segments.length, 'open line')} kept as guides. Bricks snap to them
          {selectedLineId === null ? '' : ', and one of them is selected'}.
        </p>
      )}

      <ul className="shapelist">
        {sketch.shapes.map((shape) => {
          const bounds = shapeBounds(shape);
          const fill = shape.role === 'wall' ? controller?.previewFill(shape.id) ?? null : null;
          return (
            <li key={shape.id} data-selected={selectedId === shape.id}>
              <button className="row" onClick={() => controller?.selectShape(shape.id)}>
                <span className={`dot ${shape.role}`} aria-hidden />
                <span>
                  <b>{ROLES.find((role) => role.id === shape.role)?.label}</b>
                  <span className="meta">
                    {formatMm(bounds.maxX - bounds.minX)} × {formatMm(bounds.maxY - bounds.minY)} mm ·{' '}
                    {shape.points.length} points{shape.kind === 'construction' ? ' · construction' : ''}
                  </span>
                </span>
              </button>
              <div className="shapeactions">
                <select
                  aria-label="Shape role"
                  value={shape.role}
                  onChange={(event) => controller?.setShapeRoleFor(shape.id, event.target.value as ShapeRole)}
                >
                  {ROLES.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.label}
                    </option>
                  ))}
                </select>
                {shape.role === 'wall' && shape.kind === 'wall' && (
                  <button
                    className="btn"
                    disabled={fill === null || fill.placed === 0}
                    onClick={() => controller?.fillShape(shape.id)}
                    title="Lay a bond inside this shape"
                  >
                    Fill{fill !== null && fill.placed > 0 ? ` · ${fill.placed}` : ''}
                  </button>
                )}
                <button
                  className="btn icon"
                  aria-label="Delete shape"
                  onClick={() => controller?.deleteShape(shape.id)}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </li>
          );
        })}
      </ul>

      <h2 style={{ marginTop: 14 }}>Fill bond</h2>
      <label className="field">
        <select
          aria-label="Fill bond"
          value={controller?.bondRequest.bond ?? 'stretcher'}
          onChange={(event) =>
            controller?.setBondRequest({
              ...(controller?.bondRequest ?? { bond: 'stretcher' as BondKind }),
              bond: event.target.value as BondKind,
            })
          }
        >
          {BOND_KINDS.map((bond) => (
            <option key={bond} value={bond}>
              {BOND_LABELS[bond]}
            </option>
          ))}
        </select>
      </label>
      <label className="check">
        <input
          type="checkbox"
          checked={bondCorners}
          onChange={(event) => setBondCorners(event.target.checked)}
        />
        <span>Bond the corners</span>
      </label>
      <p className="note">
        {corners === 0
          ? 'This wall meets no other, so its courses run the length you drew.'
          : `Sets the courses out from the ${corners === 1 ? 'corner' : `${corners} corners`} this wall meets: they run through to the far face of the wall next to it and stop short of it in turn, so the corner is solid and nothing overlaps.`}
      </p>
      <p className="note">
        A filled shape leaves out every window, door and flint panel. Or leave it empty and lay the
        wall by hand — bricks snap to every line either way.
      </p>

      <div className="actions">
        <button
          className="btn danger"
          disabled={sketch.shapes.length === 0 && sketch.segments.length === 0}
          onClick={() => controller?.clearSketch()}
        >
          Clear sketch
        </button>
      </div>
    </section>
  );
}
